import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from "react-router-dom";

import LoadingSpinner from '../partials/LoadingSpinner';
import DeleteSupplierAssignmentModal from './DeleteSupplierAssignmentModal';

import { H4, Panel, Box, Text, Button, Badge, Input, Tooltip, Small } from '@bigcommerce/big-design';
import { ChevronRightIcon, ChevronLeftIcon, AddIcon, CheckIcon, VisibilityIcon } from '@bigcommerce/big-design-icons';

import { assignmentsIndex, getAssignmentLocationThresholds } from '../../redux/actions/assignments';
import { purchaseOrderAddItem } from '../../redux/actions/purchaseOrders';
import { inventoryDetails } from '../../redux/actions/products';

import InventoryDetailsModal from './InventoryDetailsModal';

const AssignmentList = ({
  payload, 
  activeSupplier, 
  activeShippingAddress, 
  assignments, 
  thresholds,
  continuationToken, 
  getAssignmentsPage, 
  addItem, 
  bigcommerceSettings, 
  purchaseOrderLines, 
  getInventoryDetails,
  getRelatedThresholds,
  settings,
  }) => {
  const [page, setPage] = useState(0);
  const [showInventoryDetailsModal, setShowInventoryDetailsModal] = useState(false);
  const [activeProductId, setActiveProductId] = useState(null);
  const [activeVariantId, setActiveVariantId] = useState(null);

  const [modalProductId, setModalProductId] = useState(null);
  const [modalVariantId, setModalVariantId] = useState(null);
  const [modalOptions, setModalOptions] = useState(null);
  const [modalProductName, setModalProductName] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [modalAssignment, setModalAssignment] = useState(null);
  const [showDeleteSupplierAssignmentModal, setShowDeleteSupplierAssignmentModal] = useState(false);

  const [assignmentQuantities, setAssignmentQuantities] = useState({});

  useEffect(() => {
    getNextAssignmentPage();
  }, []);

  useEffect(() => {
    if(activeShippingAddress?.address && assignments.assignments.length > 0) {
      // Search for thresholds for each assignment
      assignments.assignments.forEach((assignment) => {
        if(assignment.variant_data) {
          // Do not search if currently searching
          if(thresholds.searchingAssignmentLocationThresholds.findIndex((key) => key === `${activeShippingAddress.id}/${assignment.variant_data.product_id}/${assignment.variant_data.id}`) >= 0) { return; }

          // Do not search if already found
          if(Object.keys(thresholds.assignmentLocationThresholds).findIndex((key) => key === `${activeShippingAddress.id}/${assignment.variant_data.product_id}/${assignment.variant_data.id}`) >= 0) { return; }

          // Search if the key doesn't exist
          getRelatedThresholds(payload, activeShippingAddress?.id, assignment?.variant_data?.product_id, assignment?.variant_data?.id)
        } else {
          // Do not search if currently searching
          if(thresholds.searchingAssignmentLocationThresholds.findIndex((key) => key === `${activeShippingAddress.id}/${assignment.product_data.id}/null`) >= 0) { return; }

          // Do not search if already found
          if(Object.keys(thresholds.assignmentLocationThresholds).findIndex((key) => key === `${activeShippingAddress.id}/${assignment.product_data.id}/null`) >= 0) { return; }

          // Search if the key doesn't exist
          if(assignment?.product_data?.id) {
            getRelatedThresholds(payload, activeShippingAddress?.id, assignment?.product_data?.id, null)
          }
        }
      })
    }
  }, [assignments, activeShippingAddress]);

  const format = (value) => {
    let formattedValue = "";

    if(bigcommerceSettings.currency_symbol_location === 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }
    
    formattedValue += parseFloat(value).toFixed(bigcommerceSettings.decimal_places)

    if(bigcommerceSettings.currency_symbol_location !== 'left') {
      formattedValue += bigcommerceSettings.currency_symbol
    }

    formattedValue = formattedValue.replace('.', bigcommerceSettings.decimal_separator)

    return formattedValue;
  }

  const handleAdd = (assignment) => {
    let itemData = {};

    if(assignment.variant_data) {
      itemData.productId = assignment.variant_data.product_id;
      itemData.variantId = assignment.variant_data.id;
      itemData.productName = assignments.relatedProducts[assignment.product_id].name;
      itemData.imageUrl = assignment?.variant_data?.imageUrl || assignments.relatedProducts[assignment.product_id]?.primary_image?.url_tiny;
      itemData.fullImageUrl = assignment?.variant_data?.fullImageUrl || assignments.relatedProducts[assignment.product_id]?.primary_image?.url_standard;
      itemData.options = assignment.variant_data.option_values;
      itemData.sku = assignment.variant_data.sku;
      itemData.upc = assignment.variant_data.upc;
      itemData.supplier_sku = assignment.supplier_sku;
      itemData.supplier_price = assignment.supplier_price;
      itemData.minimumOrder = assignment.minimum_order;

      if(settings?.settings?.order_multiples) {
        itemData.orderMultiple = assignment.order_multiple;
      }

      if(settings?.settings?.pack_quantities) {
        itemData.packQuantity = assignment.pack_quantity;
      }

      itemData.setQuantity = assignmentQuantities[`${assignment.variant_data.product_id}/${assignment.variant_data.id}`] || assignment.minimum_order;
    } else {
      itemData.productId = assignment.product_data.id;
      itemData.variantId = null;
      itemData.productName = assignment.product_data.name;
      itemData.imageUrl = assignment.product_data?.primary_image?.url_tiny;
      itemData.fullImageUrl = assignment.product_data?.primary_image?.url_standard;
      itemData.options = null;
      itemData.sku = assignment.product_data.sku;
      itemData.upc = assignment.product_data.upc;
      itemData.supplier_sku = assignment.supplier_sku;
      itemData.supplier_price = assignment.supplier_price;
      itemData.minimumOrder = assignment.minimum_order;

      if(settings?.settings?.order_multiples) {
        itemData.orderMultiple = assignment.order_multiple;
      }

      if(settings?.settings?.pack_quantities) {
        itemData.packQuantity = assignment.pack_quantity;
      }

      itemData.setQuantity = assignmentQuantities[`${assignment?.product_data?.id}/null`] || assignment.minimum_order;
    }

    addItem(payload, itemData);
  }

  const handleDeleteAssignment = (assignment) => {
    console.log(assignment);

    setModalProductId(assignment.product_id);
    setModalVariantId(assignment.variant_id);
    setModalOptions(false);
    setModalProductName('Product not found');
    setModalType(assignment.variant_id ? 'variant' : 'product');
    setModalAssignment(assignment);

    setShowDeleteSupplierAssignmentModal(true);
  }

  const showInventoryModal = (assignment) => {
    let productId = null;
    let variantId = null;

    if(assignment.variant_data) {
      productId = assignment.variant_data.product_id;
      variantId = assignment.variant_data.id;
      setActiveProductId(assignment.variant_data.product_id);
      setActiveVariantId(assignment.variant_data.id);
    } else {
      productId = assignment.product_data.id;
      setActiveProductId(assignment.product_data.id);
      setActiveVariantId(null);
    }

   getInventoryDetails(payload, productId, variantId);
   setShowInventoryDetailsModal(true);
  }

  const getPreviousAssignmentsPage = () => {
    let previousContinuationToken = assignments.assignmentsSearchStack[page - 2];
    getAssignmentsPage(payload, activeSupplier.id, previousContinuationToken, (page - 1));
    setPage(page - 1);
  }

  const getNextAssignmentPage = () => {
    getAssignmentsPage(payload, activeSupplier.id, assignments.assignmentsSearchStack[page], (page + 1));
    setPage(page + 1);
  }

  const updateAssignmentQuantityField = (event, key) => {
    let value = event.target.value;
    let newQuantities = { ...assignmentQuantities };

    newQuantities[key] = parseInt(value);
    setAssignmentQuantities(newQuantities);
  }

  return(
    <>
      <H4>Supplier Assignments</H4>

      <div className="flex-container">
        <div className="flex-1">
          <Button
            onClick={() => getPreviousAssignmentsPage()}
            disabled={page === 1}
            variant="secondary"
            iconLeft={<ChevronLeftIcon />}
          >
            Previous Assignments
          </Button>

          <Button
            onClick={() => getNextAssignmentPage()}
            disabled={assignments.lastPage}
            variant="secondary"
            iconRight={<ChevronRightIcon />}
          >
            Next Assignments
          </Button>
        </div>
      </div>

      <br/>

      <div className="full-width">
        <Box marginBottom="medium">
          { assignments.isFetching &&
            <LoadingSpinner statusText="Loading Assignments" />
          }

          { assignments.assignments.length === 0 && !assignments.isFetching &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              <div className="text-center">
                <Text>No assignments created for this supplier. Add supplier assignments to see them listed here.</Text>
                <Link to="/assignments">
                  <Button iconLeft={<AddIcon />}>
                    Manage supplier assignments
                  </Button>
                </Link>
              </div>
            </Box>
          }

          { assignments.assignments.length > 0 &&
              <div className="zebra-stripe-children">
                { assignments.assignments.map(assignment =>
                  <lineitem>
                    <div className="has-hover sku-line">
                      <H4>{ assignment.variant_id ? 'Variant' : 'Product'} - { assignment?.product_data?.name || assignments.relatedProducts[assignment.product_id]?.name }</H4>
                      <div className="flex-container">
                        <div className="left-15">
                          { assignment?.product_data?.primary_image && <img src={assignment?.product_data?.primary_image.url_tiny} /> }
                          { !assignment?.product_data?.primary_image && assignment?.variant_data?.imageUrl && <img src={assignment?.variant_data?.imageUrl} /> }
                          { !assignment?.product_data?.primary_image && !assignment?.variant_data?.imageUrl && assignments.relatedProducts[assignment.product_id]?.primary_image && <img src={assignments.relatedProducts[assignment.product_id]?.primary_image.url_tiny} /> }
                          { !assignment?.product_data?.primary_image && !assignment?.variant_data?.imageUrl && !assignments.relatedProducts[assignment.product_id]?.primary_image && <Text>No image</Text>}
                        </div>
                        <div className="flex-2">
                          <div className="left-15">
                            <H4>Assignment Details</H4>
                            <ul>
                              <li>Supplier SKU: <Badge label={assignment.supplier_sku || "Not set"} variant={assignment.supplier_sku ? "success" : "warning"} /></li>
                              <li>Supplier Price: <Badge label={assignment.supplier_price ? format(assignment.supplier_price) : "Not set"} variant={assignment.supplier_price ? "success" : "warning"} /></li>
                              <li>Minimum Order Quantity: <Badge label={assignment.minimum_order || "Not set"} variant={assignment.minimum_order ? "success" : "warning"} /></li>
                              { settings?.settings?.order_multiples && 
                                <li>Order Multiple: <Badge label={assignment.order_multiple || "Not set"} variant={assignment.order_multiple ? "success" : "warning"} /></li>
                              }
                              { settings?.settings?.pack_quantities && 
                                <li>Pack Quantity: <Badge label={assignment.pack_quantity || "Not set"} variant={assignment.pack_quantity ? "success" : "warning"} /></li>
                              }
                            </ul>
                          </div>
                        </div>

                        <div className="flex-2">
                          { !(assignment?.variant_data || assignment?.product_data) &&
                            <>
                              <H4>Item Details</H4>
                              <Small color="danger70">No matching BigCommerce data found for this assignment.</Small>
                              <Small>If this item still exists in your store, this may be a temporary error. If this item has been removed from your store, you may wish to delete this assignment.</Small>
                            </>
                          }

                          { (assignment?.variant_data || assignment?.product_data) &&
                            <>
                              <H4>Item Details</H4>
                              <ul>
                                { assignment?.variant_data?.option_values && assignment?.variant_data?.option_values.map(option =>
                                    <li>
                                      <strong>{ option.option_display_name }</strong>: <Badge label={ option.label } variant="secondary" />
                                    </li>
                                  )
                                }
                                { assignment?.product_data &&
                                  <li>
                                    <Tooltip placement="top-start" trigger={<strong>Default Location Inventory</strong>}>
                                      Default location is likely the first location form your BigCommerce store. Check BigCommerce settings and inventory levels to verify your BigCommerce location.
                                    </Tooltip>
                                    : { assignment.product_data.inventory_level }
                                  </li>
                                }
                                { assignment?.variant_data &&
                                  <li>
                                    <Tooltip placement="top-start" trigger={<strong>Default Location Inventory</strong>}>
                                      Default location is likely the first location form your BigCommerce store. Check BigCommerce settings and inventory levels to verify your BigCommerce location.
                                    </Tooltip>
                                    : { assignment.variant_data.inventory_level }
                                  </li>
                                }
                                { activeShippingAddress?.address && thresholds.assignmentLocationThresholds[`${activeShippingAddress?.id}/${assignment?.variant_data?.product_id || assignment?.product_data?.id}/${assignment?.variant_data?.id || 'null'}`] &&
                                  <li>
                                    <Text>
                                      <strong>Target Inventory</strong>: &nbsp;
                                      { thresholds.assignmentLocationThresholds[`${activeShippingAddress?.id}/${assignment?.variant_data?.product_id || assignment?.product_data?.id}/${assignment?.variant_data?.id || 'null'}`]?.target_inventory }
                                    </Text>
                                  </li>
                                }
                              </ul>
                            
                              <Button
                                variant="secondary"
                                iconLeft={<VisibilityIcon />}
                                onClick={() => showInventoryModal(assignment)}
                              >
                                View Inventory Levels
                              </Button>
                            </>
                          }
                        </div>
                        <div className="flex-1">
                          { !(assignment?.variant_data || assignment?.product_data) &&
                            <Button
                              onClick={() => handleDeleteAssignment(assignment)}
                              variant="secondary"
                              actionType="destructive"
                            >
                              Delete Assignment
                            </Button>
                          }

                          { (assignment?.variant_data || assignment?.product_data) &&
                            <div>
                                { !(Object.keys(purchaseOrderLines.lineDetails).find( (key) => (key === `${assignment?.variant_data?.product_id}/${assignment?.variant_data?.id}` || key === `${assignment?.product_data?.id}/null`) ) ) &&
                                  <>
                                    <div className="flex-container flex-end">
                                      <Text marginTop="xxSmall" as="span">QTY: &nbsp;&nbsp;</Text>
                                      <div className="width-50">
                                        <Input
                                          label=""
                                          onChange={(e) => updateAssignmentQuantityField(e, `${assignment?.variant_data?.product_id || assignment?.product_data?.id}/${assignment?.variant_data?.id || 'null'}`)}
                                          placeholder="QTY"
                                          type="number"
                                          value={assignmentQuantities[`${assignment?.variant_data?.product_id || assignment?.product_data?.id}/${assignment?.variant_data?.id || 'null'}`] || parseInt(assignment.minimum_order) || 1}
                                          required
                                        />
                                      </div>
                                    </div>

                                    <Button
                                      onClick={() => handleAdd(assignment)}
                                      variant="secondary"
                                    >
                                      Add to Purchase Order
                                    </Button>
                                  </>
                                }

                                { Object.keys(purchaseOrderLines.lineDetails).find( (key) => (key === `${assignment?.variant_data?.product_id}/${assignment?.variant_data?.id}` || key === `${assignment?.product_data?.id}/null`) ) &&
                                  <Button
                                    iconLeft={<CheckIcon />}
                                    disabled
                                    variant="secondary"
                                  >
                                    Added to Purchase Order
                                  </Button>
                                }

                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  </lineitem>
                )}
              </div>
            }

        </Box>
      </div>
      <InventoryDetailsModal
        payload={payload}
        showInventoryDetailsModal={showInventoryDetailsModal}
        setShowInventoryDetailsModal={setShowInventoryDetailsModal}
        productId={activeProductId}
        variantId={activeVariantId}
      />

      { showDeleteSupplierAssignmentModal &&
        <DeleteSupplierAssignmentModal
          payload={payload}
          showDeleteSupplierAssignmentModal={showDeleteSupplierAssignmentModal}
          setShowDeleteSupplierAssignmentModal={setShowDeleteSupplierAssignmentModal}
          supplier={activeSupplier}
          modalProductId={modalProductId}
          modalVariantId={modalVariantId}
          modalOptions={modalOptions}
          modalProductName={modalProductName}
          modalType={modalType}
          modalAssignment={modalAssignment}
        />
      }
    </>
  )

}

const mapStateToProps = state => ({
  assignments: state.assignments,
  thresholds: state.thresholds,
  continuationToken: state.assignments.assignments?.continuationToken,
  bigcommerceSettings: state.stores.settings,
  purchaseOrderLines: state.purchaseOrderLines,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  getAssignmentsPage: (payload, supplierId, continuationToken, page) => dispatch(assignmentsIndex(payload, supplierId, continuationToken, page)),
  addItem: (payload, itemData) => dispatch(purchaseOrderAddItem(payload, itemData)),
  getInventoryDetails: (payload, productId, variantId) => dispatch(inventoryDetails(payload, productId, variantId)),
  getRelatedThresholds: (payload, locationId, productId, variantId) => dispatch(getAssignmentLocationThresholds(payload, locationId, productId, variantId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssignmentList);